<template>
  <label
    :class="[
      'radio-card-selection',
      {
        'radio-card-selection--selected': isSelected,
      },
    ]"
    @click="handleClick"
  >
    <div class="radio-card-selection__content">
      <div class="radio-card-selection__bank-info">
        <img :src="bankIcon" :alt="bankName" class="radio-card-selection__bank-logo" />
        <div class="radio-card-selection__bank-name">{{ bankName }}</div>
      </div>
      <div class="radio-card-selection__account-number">Account {{ formattedAccountNumber }}</div>
    </div>
    <div class="radio-card-selection__radio">
      <div v-if="isSelected" class="radio-card-selection__radio-dot"></div>
    </div>
    <input
      type="radio"
      :name="name"
      :value="value"
      :checked="isSelected"
      class="radio-card-selection__input"
      @change="handleChange"
    />
  </label>
</template>

<script>
import { BANK_ENUM } from '@/constants/MyGiving/bank-list';

export default {
  name: 'RadioCardSelection',
  props: {
    bankCode: {
      type: String,
      required: true,
    },
    accountNumber: {
      type: String,
      required: true,
    },
    value: {
      type: [String, Number],
      required: true,
    },
    modelValue: {
      type: [String, Number],
      default: null,
    },
    name: {
      type: String,
      default: 'radio-group',
    },
  },
  emits: ['update:modelValue', 'change'],
  computed: {
    bankData() {
      return BANK_ENUM[this.bankCode] || null;
    },
    bankIcon() {
      return this.bankData?.iconPath || '';
    },
    bankName() {
      return this.bankData?.name || 'Bank';
    },
    formattedAccountNumber() {
      if (!this.accountNumber) return '';
      // Extract last 4 digits from the masked account number
      const lastFourDigits = this.accountNumber.replace(/\D/g, '').slice(-4);
      return lastFourDigits;
    },
    isSelected() {
      return this.modelValue === this.value;
    },
  },
  methods: {
    handleClick() {
      if (!this.isSelected) {
        this.$emit('update:modelValue', this.value);
        this.$emit('change', this.value);
      }
    },
    handleChange(event) {
      if (event.target.checked) {
        this.$emit('update:modelValue', this.value);
        this.$emit('change', this.value);
      }
    },
  },
};
</script>

<style scoped>
.radio-card-selection {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 80px;
  padding-top: 16px;
  padding-bottom: 16px;
  padding-left: 20px;
  padding-right: 20px;
  background-color: transparent;
  border: 1px solid #a5a4a3;
  border-radius: 10px;
  cursor: pointer;
  gap: 16px;
  font-family: 'Inter', sans-serif;
  transition:
    background-color 0.2s,
    border-color 0.2s,
    border-width 0.2s;
  box-sizing: border-box;
}

@media (min-width: 768px) {
  .radio-card-selection {
    min-height: 96px;
    padding-top: 18px;
    padding-bottom: 18px;
    padding-left: 24px;
    padding-right: 24px;
    gap: 18px;
  }
}

@media (min-width: 1024px) {
  .radio-card-selection {
    min-height: 112px;
    padding-top: 20px;
    padding-bottom: 20px;
    padding-left: 28px;
    padding-right: 28px;
    gap: 20px;
  }
}

.radio-card-selection--selected {
  background-color: rgba(232, 220, 199, 0.4);
  border: 2px solid #42362b;
}

.radio-card-selection__content {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1 1 0;
  min-width: 0;
  align-items: flex-start;
  justify-content: center;
  overflow: hidden;
}

@media (min-width: 768px) {
  .radio-card-selection__content {
    gap: 6px;
  }
}

@media (min-width: 1024px) {
  .radio-card-selection__content {
    gap: 8px;
  }
}

.radio-card-selection__bank-info {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-width: 0;
  flex-shrink: 0;
}

@media (min-width: 768px) {
  .radio-card-selection__bank-info {
    gap: 10px;
  }
}

@media (min-width: 1024px) {
  .radio-card-selection__bank-info {
    gap: 12px;
  }
}

.radio-card-selection__bank-logo {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  object-fit: contain;
}

@media (min-width: 768px) {
  .radio-card-selection__bank-logo {
    width: 32px;
    height: 32px;
  }
}

@media (min-width: 1024px) {
  .radio-card-selection__bank-logo {
    width: 40px;
    height: 40px;
  }
}

.radio-card-selection__bank-name {
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  font-size: 16px;
  line-height: 24px;
  color: #21201e;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1 1 0;
  min-width: 0;
}

@media (min-width: 768px) {
  .radio-card-selection__bank-name {
    font-size: 24px;
    line-height: 32px;
  }
}

@media (min-width: 1024px) {
  .radio-card-selection__bank-name {
    font-size: 26px;
    line-height: 36px;
  }
}

.radio-card-selection__account-number {
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #373531;
  height: 20px;
  width: 100%;
  display: flex;
  align-items: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (min-width: 768px) {
  .radio-card-selection__account-number {
    font-size: 22px;
    line-height: 28px;
    height: 28px;
  }
}

@media (min-width: 1024px) {
  .radio-card-selection__account-number {
    font-size: 24px;
    line-height: 32px;
    height: 32px;
  }
}

.radio-card-selection__radio {
  position: relative;
  width: 16px;
  height: 16px;
  border: 1px solid #42362b;
  border-radius: 50%;
  flex-shrink: 0;
}

@media (min-width: 768px) {
  .radio-card-selection__radio {
    width: 20px;
    height: 20px;
  }
}

@media (min-width: 1024px) {
  .radio-card-selection__radio {
    width: 24px;
    height: 24px;
  }
}

.radio-card-selection__radio-dot {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 6.4px;
  height: 6.4px;
  background-color: #42362b;
  border-radius: 50%;
}

@media (min-width: 768px) {
  .radio-card-selection__radio-dot {
    width: 8px;
    height: 8px;
  }
}

@media (min-width: 1024px) {
  .radio-card-selection__radio-dot {
    width: 9.6px;
    height: 9.6px;
  }
}

.radio-card-selection__input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}
</style>
